import { Suspense, lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AuthBoundary } from '@/features/auth/AuthBoundary';
import { RequireAuth } from '@/features/auth/require-auth';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageLoader } from '@/components/PageLoader';
import { PublicLayout } from '@/layouts/PublicLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { HomePage } from '@/pages/public/HomePage';

/** Code-splits a route: visitors never download the admin bundle. */
const page = (loader: () => Promise<{ default: ComponentType }>) => {
  const Component = lazy(loader);
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'projekte', element: page(() => import('@/pages/public/ProjectsPage')) },
      { path: 'projekte/:slug', element: page(() => import('@/pages/public/ProjectDetailPage')) },
      { path: 'impressum', element: page(() => import('@/pages/public/ImprintPage')) },
      { path: 'datenschutz', element: page(() => import('@/pages/public/PrivacyPage')) },
    ],
  },
  {
    path: '/admin',
    element: <AuthBoundary />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: 'login', element: page(() => import('@/pages/admin/LoginPage')) },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <Navigate to="/admin/dashboard" replace /> },
              { path: 'dashboard', element: page(() => import('@/pages/admin/DashboardPage')) },
              { path: 'leads', element: page(() => import('@/pages/admin/LeadsPage')) },
              { path: 'leads/:id', element: page(() => import('@/pages/admin/LeadDetailPage')) },
              { path: 'projects', element: page(() => import('@/pages/admin/ProjectsPage')) },
              { path: 'content', element: page(() => import('@/pages/admin/ContentPage')) },
              { path: 'settings', element: page(() => import('@/pages/admin/SettingsPage')) },
              { path: 'users', element: page(() => import('@/pages/admin/UsersPage')) },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <ErrorBoundary /> },
]);
